/**
 * API Client
 * 
 * A centralized client for making API requests with consistent error handling,
 * authentication, and parameter substitution.
 */

import axios from 'axios';
import { config } from '../constants/config';
import { endSession } from '../utils/sessionBridge';

// Create axios instance with default config
const api = axios.create({
  baseURL: config.BASE_URL || '',
  timeout: config.REQUEST_TIMEOUT || 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * ⭐ NAMED TIMEOUTS, because the default is a 30-second ceiling and some calls are not 30-second
 * calls. A call site reading `TIMEOUTS.AUDITION` says what it is waiting for; a bare `180000` does
 * not, and the number gets copied without its reason.
 *
 * ⚠️ THE DEFAULT IS THE HAZARD THIS EXISTS FOR. `docs/journal/CLIENT_API_CLIENT_MIGRATION_PLAN.md`
 * §1.4 records it as the single most important constraint on migrating anything to this client: a
 * find-and-replace sweep would impose 30 seconds on the import, render, stitch and master paths at
 * once — reproducing the Admin delete that took 195 seconds, SUCCEEDED, and was reported to the
 * operator as an error. It has now caught one more caller: the pronunciation audition renders six
 * TTS takes and cannot finish inside 30s, which surfaced as a bare "Audition failed" with no
 * response to read.
 */
export const TIMEOUTS = Object.freeze({
  DEFAULT: config.REQUEST_TIMEOUT || 30000,
  /** Several TTS generations back to back — six takes of ~700 characters. */
  AUDITION: 300000,
  /** Anything that re-reads a book: ingest preview, re-ingest kick-off, script loads. */
  INGEST: 900000,
});

// Set once when a 401 sends us to login, so the several requests a page fires concurrently cannot
// each trigger their own navigation and interrupt the first. Reset by the page load that follows.
let redirectingToLogin = false;

/** localStorage key holding the FIRST 401 of a session-loss event. Survives the redirect. */
export const AUTH_BREADCRUMB_KEY = 'authFailureBreadcrumb';

/**
 * ─── ⭐ WHAT ACTUALLY ENDED THE SESSION, WRITTEN DOWN BEFORE THE EVIDENCE IS DESTROYED ───────────
 *
 * This exists because the logout bug has been "cured" several times and kept coming back looking
 * different. The reason it kept hiding: the 401 handler deleted the token, which made every other
 * request fail the same way, so the console filled with identical copies of the CONSEQUENCE and the
 * first error — the only one that names a cause — scrolled away. Then the redirect wiped the console.
 *
 * ⚠️ ONE ENTRY PER EVENT, AND A SHORT HISTORY THAT LOGGING IN DOES NOT ERASE. The first 401 is the
 * cause; the rest are the cascade, so entries inside `EVENT_WINDOW_MS` of the last are dropped. And
 * it deliberately does NOT clear on login: the operator logs straight back in after being ejected,
 * so a crumb cleared at login would be gone before it could be read. Keeping the last few means the
 * record survives days of recurrence and can be compared across them.
 *
 * ⛔ NEVER RECORDS THE TOKEN. Length, expiry and shape are enough to tell "absent" from "expired"
 * from "poisoned", and a credential in localStorage-as-diagnostics is a credential in a bug report.
 */
const EVENT_WINDOW_MS = 30 * 1000;
const BREADCRUMB_MAX = 8;

/**
 * Describe a token WITHOUT recording it. Length, part count and expiry separate "absent" from
 * "expired" from "poisoned", which is everything a diagnosis needs.
 *
 * ⚠️ Takes the value rather than reading storage, because the client-side caller below has
 * already had to remove it: a shape computed after the removal is always "absent", which is the
 * one answer that explains nothing.
 */
/**
 * The JWT payload, or null if it cannot be read. ⛔ NEVER VERIFIES — that is the server's job and
 * cannot be done here. This reads claims the client is allowed to act on for itself: when to stop
 * trusting a token it already holds.
 */
function decodeTokenPayload(raw) {
  if (!raw || raw === 'null' || raw === 'undefined') return null;
  try {
    const part = raw.split('.')[1];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
  } catch (_) { return null; }
}

function describeToken(raw) {
  let tokenShape = 'absent';
  let expiresAt = null;
  if (raw === 'null' || raw === 'undefined') {
    tokenShape = `poisoned:"${raw}"`;
  } else if (raw) {
    tokenShape = `present len=${raw.length} parts=${raw.split('.').length}`;
    const payload = decodeTokenPayload(raw);
    if (!payload) {
      tokenShape += ' (payload unreadable)';
    } else if (typeof payload.exp === 'number') {
      expiresAt = new Date(payload.exp * 1000).toISOString();
    }
  }
  return { tokenShape, expiresAt };
}

/**
 * ─── ⭐⭐ NEVER STORE A TOKEN YOU CAN SEE IS DEAD (2026-10-26) ────────────────────────────────────
 *
 * The sliding-session refresh below took `X-Refresh-Token` off any response and wrote it to
 * storage unconditionally. ⛔ That trusts a response header to be fresh, and a response header is
 * not necessarily live: the server only issues one when the presented token has under 6h left
 * (middleware/auth.js), so the header ALWAYS carries a token minted near the end of a session —
 * and no API response sets `Cache-Control: no-store`, which makes that credential cacheable.
 *
 * The failure that follows is brutal to diagnose and was observed twice on 2026-10-26, at 8:57pm
 * and 9:07pm: log in, receive a perfectly good 12-hour token, click one feature link, and a
 * replayed response writes a months-stale JWT over it. The next render finds an expired token,
 * refuses it before any request goes out, and reports `token-not-live` — which reads as "your
 * sign-in lapsed, nothing is wrong" when in fact a live session was destroyed a minute after
 * starting. ⭐ It also explains the operator's long-standing report that a failure "seems to log
 * in, but clicking on any link kicks back to login".
 *
 * ⓘ THE GUARD IS CORRECT INDEPENDENTLY OF THAT DIAGNOSIS, which is why it is worth having before
 * the cause is proven: a token we can plainly read as expired, or as no newer than the one in
 * hand, is never an improvement on what we already hold.
 */
export const REFRESH_REFUSED = {
  UNREADABLE: 'refresh-token-unreadable',
  EXPIRED: 'refresh-token-expired',
  NOT_NEWER: 'refresh-token-not-newer',
  /**
   * ⭐ A CONCURRENT DUPLICATE, WHICH IS THE ORDINARY CASE AND NOT A FINDING (2026-10-27).
   *
   * Measured on production: one visit to /admin/reader-access fired FIVE requests inside 142ms,
   * each passing the auth middleware independently, each therefore carrying its own newly minted
   * refresh token. JWT `exp` is in whole seconds, so tokens minted in the same second have the
   * IDENTICAL expiry — the first to land is stored and the other four are equivalent to it.
   *
   * ⛔ Lumping these in with NOT_NEWER made every page load in the refresh window write four
   * refusals, and both rings hold eight. A single page view would have evicted the evidence of a
   * genuine stale replay — the same disease as the no-op token writes fixed the day before.
   *
   * ⓘ Which is why the distinction is worth drawing at all: an exp EARLIER than ours is a stale
   * token being replayed, the thing this guard exists to catch. An exp EQUAL to ours is five
   * requests agreeing with each other.
   */
  DUPLICATE: 'refresh-token-duplicate',
};

/**
 * Should this re-issued token replace the one in storage?
 *
 * ⚠️ `<=` on the expiry, not `<`. A replay of the token we already hold is not an improvement, and
 * treating it as one would hide exactly the case this exists to catch.
 */
export function acceptRefreshedToken(refreshed, current) {
  const held = arguments.length > 1 ? current : localStorage.getItem('authToken');
  const next = decodeTokenPayload(refreshed);
  if (!next || typeof next.exp !== 'number') {
    return { ok: false, reason: REFRESH_REFUSED.UNREADABLE };
  }
  if (next.exp * 1000 <= Date.now()) {
    return { ok: false, reason: REFRESH_REFUSED.EXPIRED };
  }
  const heldPayload = decodeTokenPayload(held);
  if (heldPayload && typeof heldPayload.exp === 'number') {
    // Equal first: the common case, and the one that must not read as a stale replay.
    if (next.exp === heldPayload.exp) {
      return { ok: false, reason: REFRESH_REFUSED.DUPLICATE };
    }
    if (next.exp < heldPayload.exp) {
      return { ok: false, reason: REFRESH_REFUSED.NOT_NEWER };
    }
  }
  return { ok: true, reason: null };
}

/**
 * ⚠️ A REFUSAL GETS ITS OWN STORE, NOT THE SESSION-LOSS RING.
 *
 * Two reasons, and the second is the one that matters. A refused refresh is not a session loss —
 * the good token was kept and nothing broke — so it would be a non-event in the panel that
 * explains why you are looking at a login form. And pushCrumb drops anything arriving within
 * EVENT_WINDOW_MS of the previous entry: a refusal written to the ring could swallow a genuine
 * breadcrumb thirty seconds later, destroying the evidence instead of adding to it.
 *
 * ⛔ Shapes only, never the token — the same rule the breadcrumbs follow.
 */
export const REFRESH_REFUSAL_KEY = 'authRefreshRefusals';

function recordRefreshRefusal(reason, refreshed, held, url) {
  try {
    let history = [];
    try { history = JSON.parse(localStorage.getItem(REFRESH_REFUSAL_KEY)) || []; } catch (_) { history = []; }
    if (!Array.isArray(history)) history = [];
    const offered = describeToken(refreshed);
    const kept = describeToken(held);
    const entry = {
      at: new Date().toISOString(),
      reason,
      url: url || null,
      page: window?.location?.pathname || null,
      offeredShape: offered.tokenShape,
      offeredExpiresAt: offered.expiresAt,
      keptShape: kept.tokenShape,
      keptExpiresAt: kept.expiresAt,
    };
    history.push(entry);
    while (history.length > BREADCRUMB_MAX) history.shift();
    localStorage.setItem(REFRESH_REFUSAL_KEY, JSON.stringify(history));
    console.warn('🔑 REFUSED A RE-ISSUED TOKEN — the session was NOT ended. Paste this:', entry);
  } catch (_) { /* nothing here may throw into an interceptor */ }
}

/** The refused re-issued tokens, newest last. For the console: `readRefreshRefusals()`. */
export function readRefreshRefusals() {
  try { return JSON.parse(localStorage.getItem(REFRESH_REFUSAL_KEY)) || []; } catch (_) { return []; }
}

/**
 * ─── ⚠️ TEMPORARY: EVERY ARRIVAL OF X-REFRESH-TOKEN, ACCEPTED OR NOT ────────────────────────────
 *
 * The refusal log above only fires when the guard rejects something, which proves nothing if the
 * guard is never tripped. This records every arrival, so we learn whether the header turns up at
 * all, on which request, and — the question the whole theory rests on — whether that response came
 * off the wire or out of a cache.
 *
 * ⓘ Resource Timing is the measurement: a response served from cache has `transferSize === 0`
 * while still having a decoded body. It is not available for every request (a cross-origin
 * response without Timing-Allow-Origin reports zeroes), hence `null` for "could not tell" rather
 * than a misleading `false`.
 *
 * ⛔ Remove with the token audit once the cause is known.
 */
export const REFRESH_ARRIVAL_KEY = 'authRefreshArrivals';

/**
 * ⭐⭐ HOW OLD IS THE CREDENTIAL WE WERE JUST HANDED (2026-10-27)?
 *
 * This is the measurement that matters, and it took a failed one to find it. `fromCache` below
 * came back **null** on production, which is correct and useless: the API is a different origin
 * (api.authorator-ai.com), and a cross-origin response without `Timing-Allow-Origin` reports
 * transferSize and decodedBodySize as 0, so Resource Timing cannot tell a cached response from a
 * fetched one. ⛔ Do not read that null as "not cached".
 *
 * The refresh token is itself a JWT, so it states when it was minted. A token handed over by a
 * live server is seconds old; one replayed out of a cache is hours old. ⓘ No server header, no
 * CORS change, and it measures the thing we actually care about rather than a proxy for it.
 */
function tokenAgeSeconds(raw) {
  const payload = decodeTokenPayload(raw);
  if (!payload || typeof payload.iat !== 'number') return null;
  return Math.round(Date.now() / 1000 - payload.iat);
}

function wasServedFromCache(url) {
  try {
    const entries = performance.getEntriesByType('resource')
      .filter(e => url && e.name && e.name.indexOf(url) !== -1);
    const last = entries[entries.length - 1];
    if (!last) return null;
    if (typeof last.transferSize !== 'number' || typeof last.decodedBodySize !== 'number') return null;
    if (last.transferSize === 0 && last.decodedBodySize === 0) return null; // opaque timing
    return last.transferSize === 0;
  } catch (_) { return null; }
}

function recordRefreshArrival(refreshed, held, verdict, url) {
  try {
    let history = [];
    try { history = JSON.parse(localStorage.getItem(REFRESH_ARRIVAL_KEY)) || []; } catch (_) { history = []; }
    if (!Array.isArray(history)) history = [];
    const offered = describeToken(refreshed);
    const kept = describeToken(held);
    history.push({
      at: new Date().toISOString(),
      url: url || null,
      page: window?.location?.pathname || null,
      accepted: !!verdict.ok,
      refusedBecause: verdict.reason || null,
      /**
       * ⭐ THE FIELD TO READ. Seconds old at the moment it arrived: a few seconds means a live
       * server minted it for this request; hours means something replayed a credential. ⛔ Read
       * this rather than `fromCache`, which is null for any cross-origin API.
       */
      offeredAgeSeconds: tokenAgeSeconds(refreshed),
      fromCache: wasServedFromCache(url),
      offeredExpiresAt: offered.expiresAt,
      keptExpiresAt: kept.expiresAt,
      offeredShape: offered.tokenShape,
    });
    while (history.length > BREADCRUMB_MAX) history.shift();
    localStorage.setItem(REFRESH_ARRIVAL_KEY, JSON.stringify(history));
  } catch (_) { /* nothing here may throw into an interceptor */ }
}

/** Every X-Refresh-Token arrival, newest last. For the console: `readRefreshArrivals()`. */
export function readRefreshArrivals() {
  try { return JSON.parse(localStorage.getItem(REFRESH_ARRIVAL_KEY)) || []; } catch (_) { return []; }
}

/**
 * Append one crumb, newest last, collapsing a cascade into its cause.
 *
 * ⚠️ THE DEDUPE IS LOAD-BEARING FOR THE CLIENT-SIDE CALLER TOO. useIsLoggedIn runs in every
 * component that gates on a session — a dozen or more in one render pass — so without the
 * EVENT_WINDOW_MS guard a single expiry would fill all eight slots with copies of itself and
 * evict the history it exists to preserve.
 */
function pushCrumb(crumb, headline) {
  try {
    let history = [];
    try { history = JSON.parse(localStorage.getItem(AUTH_BREADCRUMB_KEY)) || []; } catch (_) { history = []; }
    if (!Array.isArray(history)) history = [];
    // Same event → this is the cascade, and the cause is already recorded.
    const last = history[history.length - 1];
    if (last && Date.now() - new Date(last.at).getTime() < EVENT_WINDOW_MS) return false;

    history.push(crumb);
    while (history.length > BREADCRUMB_MAX) history.shift();
    localStorage.setItem(AUTH_BREADCRUMB_KEY, JSON.stringify(history));
    // Loud, and once per event. This is the line worth pasting into a bug report.
    console.warn(headline, crumb);
    return true;
  } catch (_) { /* storage unavailable; the console.warn above is the fallback */ }
  return false;
}

function recordAuthBreadcrumb(error) {
  try {
    const { tokenShape, expiresAt } = describeToken(localStorage.getItem('authToken'));
    pushCrumb({
      at: new Date().toISOString(),
      url: error.config?.url || null,
      method: error.config?.method?.toUpperCase() || null,
      // ⭐ The two fields that answer the whole question.
      sentAuthHeader: error.config?.__sentAuth !== false,
      serverReason: error.response?.data?.reason || null,
      authAbsentReason: error.config?.__authAbsentReason || null,
      serverError: error.response?.data?.error || null,
      tokenShape,
      expiresAt,
      expired: expiresAt ? new Date(expiresAt).getTime() < Date.now() : null,
      page: window?.location?.pathname || null,
    }, '🔑 FIRST 401 OF THIS SESSION LOSS — paste this:');
  } catch (_) { /* nothing here may throw into an interceptor */ }
}

/**
 * ⭐⭐ THE CRUMB FOR A SESSION THAT ENDED WITHOUT A REQUEST (2026-10-26).
 *
 * Every breadcrumb until now came from a 401, which means every one required a request to have
 * gone out. But the commonest way a session actually ends leaves no request at all: the laptop
 * sleeps, the token lapses, and on wake the render-time check in useIsLoggedIn refuses it before
 * anything is sent. ⛔ So the clearest case produced NO evidence — no 401, no crumb, and a login
 * page that could not say why it was showing. The operator's console on 2026-10-26 is the record:
 * ERR_NETWORK_IO_SUSPENDED, six SSE reconnects, then ProtectedRoute redirecting, and nothing in
 * the breadcrumb history to match it.
 *
 * ⓘ sentAuthHeader is null rather than false: nothing was sent because nothing was ASKED, which
 * is a third state and must not read as "we sent no token to a server that then refused us".
 */
export function recordClientAuthEvent(reason, rawToken) {
  try {
    const { tokenShape, expiresAt } = describeToken(rawToken);
    return pushCrumb({
      at: new Date().toISOString(),
      url: null,
      method: null,
      sentAuthHeader: null,
      clientReason: reason || null,
      serverReason: null,
      authAbsentReason: null,
      serverError: null,
      tokenShape,
      expiresAt,
      expired: expiresAt ? new Date(expiresAt).getTime() < Date.now() : null,
      page: window?.location?.pathname || null,
    }, '🔑 SESSION ENDED BEFORE ANY REQUEST — paste this:');
  } catch (_) { return false; }
}

/**
 * ─── ⭐ A SIGN-OUT THE USER CHOSE NEEDS NO EXPLAINING (2026-10-26) ───────────────────────────────
 *
 * The operator signed out on dev and the login page told him "You were not signed in on this
 * device, so the request went out without a token. Nothing is wrong." Every word of it true, and
 * all of it beside the point: he had just pressed Sign Out. ⛔ A panel headed "Why you were signed
 * out" appearing under a button you deliberately pressed is noise at best, and at worst it teaches
 * you to stop reading the panel — which costs us the one case it exists for.
 *
 * ⓘ sessionStorage, not localStorage: the marker has to survive the document load that Logout now
 * does, and must NOT survive the tab. A logout in one tab explains nothing about a login in
 * another, and a stale marker here would silence a real breadcrumb.
 */
export const DELIBERATE_SIGNOUT_KEY = 'signedOutDeliberately';

/** Called by Logout, immediately before the redirect. */
export function markDeliberateSignOut() {
  try { sessionStorage.setItem(DELIBERATE_SIGNOUT_KEY, String(Date.now())); } catch (_) { /* storage unavailable */ }
}

/**
 * ⚠️ READ AND CLEAR ARE SEPARATE ON PURPOSE. A combined read-and-clear would be a side effect in
 * render, and StrictMode double-invokes a component body in development: the first call would
 * consume the marker and the second would find it gone and show the panel anyway — the exact bug
 * this is meant to fix, visible only on dev, which is where the operator works.
 */
export function readDeliberateSignOut() {
  try { return sessionStorage.getItem(DELIBERATE_SIGNOUT_KEY) !== null; } catch (_) { return false; }
}

/** Called from an effect once the panel has had its answer. */
export function clearDeliberateSignOut() {
  try { sessionStorage.removeItem(DELIBERATE_SIGNOUT_KEY); } catch (_) { /* storage unavailable */ }
}

/** The recorded 401 history, newest last. For the console: `readAuthBreadcrumbs()`. */
export function readAuthBreadcrumbs() {
  try { return JSON.parse(localStorage.getItem(AUTH_BREADCRUMB_KEY)) || []; } catch (_) { return []; }
}

/**
 * Clear the history once a cause has been identified and fixed.
 *
 * ⓘ Says what it did, because a console helper that returns nothing prints `undefined` and that
 * reads as a failure.
 */
export function clearAuthBreadcrumbs() {
  try {
    const had = readAuthBreadcrumbs().length;
    localStorage.removeItem(AUTH_BREADCRUMB_KEY);
    return `cleared ${had} breadcrumb${had === 1 ? '' : 's'}`;
  } catch (_) { return 'cleared (storage unavailable)'; }
}

// ⭐ Reachable from the browser console without importing anything, because that is where the
// operator will be standing the moment after being ejected.
if (typeof window !== 'undefined') {
  window.readAuthBreadcrumbs = readAuthBreadcrumbs;
  window.clearAuthBreadcrumbs = clearAuthBreadcrumbs;
  window.readRefreshRefusals = readRefreshRefusals;
  window.readRefreshArrivals = readRefreshArrivals;
}

/**
 * Request interceptor
 * - Adds authentication token to requests
 * - Logs outgoing requests in development
 */
api.interceptors.request.use(
  config => {
    // Add auth token if available.
    //
    // The key is the literal 'authToken', as everywhere else that touches it. This read used to be
    // `config.STORAGE_TOKEN_KEY || 'authToken'` — but `config` here is the axios REQUEST config,
    // not the app config imported above, which it shadows. That property is always undefined, so it
    // has only ever worked by falling through to the default.
    const token = localStorage.getItem('authToken');
    // A previous logout wrote the STRING "null" (setItem stringifies), and "null" is truthy — so
    // every request carried `Authorization: Bearer null` and came back 401 with no way out but
    // clearing storage by hand. The logout is fixed, but browsers already holding that value need
    // to recover on their own.
    if (token && token !== 'null' && token !== 'undefined') {
      config.headers.Authorization = `Bearer ${token}`;
      config.__sentAuth = true;
    } else {
      /**
       * ─── ⭐ REMEMBER THAT WE SENT NOTHING ──────────────────────────────────────────────────────
       *
       * The 401 handler below needs this. A 401 on a request that CARRIED a token means the session
       * is over; a 401 on a request that carried NOTHING means only that this request had no token
       * to send — a client-side race, or storage that was already empty. Ending the session over the
       * second case is what turned one stray request into the operator's recurring logout loop.
       *
       * `__sentAuth` also records WHY nothing was sent, which is the difference between "signed out"
       * and "storage holds a poisoned value" — the latter being a bug to fix rather than a state.
       */
      config.__sentAuth = false;
      config.__authAbsentReason = token === null || token === undefined
        ? 'no-token-in-storage'
        : `poisoned-token-value:${String(token)}`;
    }

    // Log requests in development
    // if (process.env.NODE_ENV === 'development') {
    //   console.log(`🚀 API Request: ${config.method.toUpperCase()} ${config.url}`, config);
    // }

    return config;
  },
  error => {
    // Log request errors
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Handles common error scenarios (401, 403, 500)
 * - Logs responses in development
 * - Could add token refresh logic here
 */
api.interceptors.response.use(
  response => {
    // Sliding session: if the server re-issued a token (it does so when the
    // current one is nearing expiry), store it so active use — e.g. a long
    // render polling for progress — keeps the session alive indefinitely.
    const refreshed = response.headers && response.headers['x-refresh-token'];
    if (refreshed) {
      // ⭐ Guarded since 2026-10-26 — see acceptRefreshedToken above. A header is not evidence of
      // freshness, and a cached response can replay a credential long after it died.
      const held = localStorage.getItem('authToken');
      const verdict = acceptRefreshedToken(refreshed, held);
      /**
       * ⛔ A CONCURRENT DUPLICATE IS NOT LOGGED, IN EITHER STORE. Five of them arrive per page load
       * inside the refresh window and both rings hold eight, so logging them would evict the
       * genuine event within one page view. See REFRESH_REFUSED.DUPLICATE for the measurement.
       */
      const worthRecording = verdict.reason !== REFRESH_REFUSED.DUPLICATE;
      if (verdict.ok) {
        try { localStorage.setItem('authToken', refreshed); } catch (_) { /* storage unavailable */ }
      } else if (worthRecording) {
        recordRefreshRefusal(verdict.reason, refreshed, held, response.config?.url);
      }
      /**
       * ⭐ RECORDED WHETHER OR NOT IT WAS ACCEPTED, and temporary. The suspicion is that this
       * header can arrive on a CACHED response, replaying a credential minted near the end of an
       * earlier session. `wasServedFromCache` is the measurement that settles it — a cached
       * response transfers no bytes over the wire while still having a body.
       */
      if (worthRecording) recordRefreshArrival(refreshed, held, verdict, response.config?.url);
    }

    return response;
  },
  async error => {
    /**
     * ─── ⭐ A BLOB BODY IS AN UNREADABLE ERROR, AND IT HID A HELPFUL MESSAGE ─────────────────────
     *
     * Routes that stream a file are requested with `responseType: "blob"`, so when they fail the
     * server's JSON error arrives as a Blob. The console then printed `data: Blob {size: 136}` and the
     * message inside it was lost.
     *
     * ⚠️ That is not academic. On 2026-09-27 a whole-book selection stitch returned
     * `TOO_MANY_LINES: "2075 lines selected; stitch at most 1000 — use Stitch for a whole unit"` — the
     * fix, named in the body — and all the operator could see was a bare 400.
     *
     * ⭐ Normalised HERE rather than at each caller, so `error.response.data` has its usual shape for
     * everyone. `stitch-selection` already unpacked the Blob by hand; nothing else did, and nothing
     * else should have to.
     */
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        try { error.response.data = JSON.parse(text); }
        catch (_) { error.response.data = { error: { message: text } }; }
      } catch (_) { /* unreadable blob — leave it as it was */ }
    }

    // Extract response for easier access
    const response = error.response;
    // Module-level latch, declared below the interceptor — see the 401 case.

    // A CANCELLED REQUEST IS NOT AN ERROR, AND SAYING SO COSTS US THE ONES THAT ARE.
    //
    // useCharacterContext aborts its in-flight fetch whenever the selection changes — correct
    // behaviour, it stops a stale response overwriting a fresh one — and every abort arrived here
    // as "❌ API Response Error". Changing a dropdown printed three red errors describing healthy
    // code. An axios cancellation carries no `response`, so it logged `status: undefined,
    // data: undefined`, which is ALSO what a timeout and a dead server look like: the console made
    // the harmless case indistinguishable from the two that matter.
    //
    // That is the failure mode that let an expired token sit behind a wall of 401s nobody read.
    // A console only gets believed if it stays quiet when nothing is wrong.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    if (!response) {
      // No response at all — the request never completed. Named explicitly, because
      // `status: undefined` reads like a bug in the logging rather than a description of what
      // happened. ECONNABORTED is the timeout; anything else is the server being unreachable.
      console.error('❌ API No Response:', {
        reason: error.code === 'ECONNABORTED' ? `timed out after ${error.config?.timeout ?? '?'}ms` : 'server unreachable or connection reset',
        code: error.code,
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
      });
      return Promise.reject(error);
    }

    // AN EXPECTED STATUS IS PART OF THE PROTOCOL, NOT A FAULT.
    //
    // Deleting a prototype returns 409 when a published Literary Group still reads its credits.
    // That is the DESIGNED path — the caller catches it and turns it into "delete the group
    // first" — and it printed a red ❌ every time, describing working code. A caller opts out per
    // request with `expectedStatuses: [409]`; everything undeclared still logs. Deliberately
    // opt-in and per-call rather than a global list, because a 409 nobody planned for is a real
    // error and must keep shouting.
    const expected = error.config?.expectedStatuses;
    if (Array.isArray(expected) && expected.includes(response.status)) {
      return Promise.reject(error);
    }

    // Log error responses
    console.error('❌ API Response Error:', {
      status: response?.status,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      data: response?.data
    });

    // Handle specific error cases
    if (response) {
      switch (response.status) {
        case 401: { // Unauthorized — the token is missing, malformed, or expired
          // THIS USED TO ONLY console.warn, and the comment said "could redirect to login".
          // It didn't. So an expired token produced a wall of 401s in a console nobody has open,
          // the dead token stayed in localStorage, and every subsequent request failed the same
          // way with nothing on screen to explain it.
          //
          // It is not a rare case. The token lives 12 hours and only auto-refreshes on a request
          // made while under 6 hours remain — so an overnight gap lapses it rather than renewing
          // it, and the next morning starts like this. Clear it and go to login.

          // ⭐ EXCEPT WHERE THE CALL NEVER CLAIMED TO BE AUTHENTICATED.
          //
          // Two different things arrive here as `401`, and treating them alike is a bug waiting on
          // a public page: a SESSION-BEARING call failing means the session is over; an
          // OPTIONAL-AUTH call failing means only that this viewer is anonymous, which is a
          // perfectly good state for an info page to render. Evicting a signed-in reader because a
          // public panel asked a question it did not need an answer to would be absurd — and with
          // an unconditional handler it is exactly what would happen.
          //
          // Callers opt out per request: `apiClient.get(url, params, query, { optionalAuth: true })`.
          // The error still REJECTS, so the caller can fall back to the anonymous view; the only
          // thing suppressed is ending the session on its behalf.
          if (error.config && error.config.optionalAuth) {
            break;
          }

          // ⭐ RECORD THE FIRST 401 BEFORE ANYTHING IS WIPED. The cascade below used to destroy its
          // own evidence: the token went, every in-flight request then failed identically, and the
          // console filled with copies of the consequence while the CAUSE scrolled past. Kept as the
          // first one only — later entries are the cascade, and overwriting would lose the cause.
          recordAuthBreadcrumb(error);

          /**
           * ─── ⛔ A REQUEST THAT SENT NO TOKEN IS NO EVIDENCE THE SESSION ENDED ────────────────────
           *
           * THE BUG THIS FIXES, and it survived several attempts because it is an amplifier rather
           * than a trigger. Selecting a LitGroup fires many requests at once. ONE of them 401s for
           * any reason; this handler deleted the token immediately; every other request — in flight
           * or fired after — then went out with no Authorization header and came back
           * `401 no-credentials`, each one arriving here to delete and redirect again.
           *
           * So one stray 401 was guaranteed to become a full logout, the operator's real first error
           * was buried under identical copies of its own consequence, and logging in again simply
           * armed the next one. "I might get farther before being kicked out" is just how many
           * requests landed before the first failure.
           *
           * ⭐ The rule: only a request that actually PRESENTED a token can prove the session is
           * dead. If we sent nothing, we learned nothing — reject, so the caller can handle it, and
           * leave the session alone. Whether the user is really signed out is the router's question,
           * and `useIsLoggedIn` already answers it from the token's own expiry.
           */
          const sentAuth = error.config?.__sentAuth !== false;
          const serverReason = response?.data?.reason;
          if (!sentAuth || serverReason === 'no-credentials' || serverReason === 'empty-credentials') {
            // ⚠️ A poisoned value ("null"/"undefined" as strings) IS cleaned — it is corrupt rather
            // than absent, and leaving it makes every later request fail the same silent way.
            if (error.config?.__authAbsentReason?.startsWith('poisoned-token-value')) {
              try { localStorage.removeItem('authToken'); } catch (_) { /* storage unavailable */ }
              // ⓘ A corrupt credential ends the session as surely as a dead one: the store must
              // not keep claiming we know who this is.
              endSession('poisoned-token-value');
            }
            console.warn(
              '401 on a request that sent no token — NOT ending the session. '
              + `url=${error.config?.url} reason=${error.config?.__authAbsentReason || serverReason}. `
              + 'See localStorage["authFailureBreadcrumb"].'
            );
            break;
          }

          try { localStorage.removeItem('authToken'); } catch (_) { /* storage unavailable */ }
          // ⭐ The store goes with the token — see utils/sessionBridge.js. Without this, the eject
          // navigates to /login with every slice still populated from the session just ended.
          endSession('server-rejected-token');
          // A page load fires several requests at once; without this guard each 401 would race to
          // navigate and the first one would be interrupted mid-flight.
          if (!redirectingToLogin && window?.location?.pathname !== '/login') {
            redirectingToLogin = true;
            console.warn(`Session expired (${serverReason || 'unknown'}) — returning to login.`);
            window.location.assign('/login');
          }
          break;
        }

        case 403: // Forbidden
          console.warn('Permission denied: You do not have access to this resource');
          break;

        case 500: // Server error
          console.error('Server error occurred');
          break;

        default:
          // Other status codes handled by the caller
          break;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Substitutes URL parameters with values from the params object
 * 
 * @param {string} url - URL template with parameters in :param format
 * @param {object} params - Object containing parameter values
 * @returns {string} URL with parameters substituted
 * 
 * @example
 * substituteParams('/api/users/:userId', { userId: '123' })
 * // Returns: '/api/users/123'
 */
export const substituteParams = (url, params = {}) => {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }

  let result = url;

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Replace all occurrences of the parameter
      const regex = new RegExp(`:${key}(?=/|$)`, 'g');
      result = result.replace(regex, encodeURIComponent(value));
    }
  });

  return result;
};

/**
 * API client with standardized methods for API calls
 */
/**
 * The underlying axios instance.
 *
 * Exported so the SESSION BEHAVIOUR — 401 recovery, the redirect latch, the sliding-session
 * refresh — can be tested by swapping `api.defaults.adapter`, which answers every request
 * in-process. Those behaviours live on the interceptors, which belong to the instance rather than
 * to the wrapper below, and none of them had a test before.
 *
 * ⚠️ Application code should use `apiClient`, not this: the wrapper is what applies PathDef
 * parameter substitution.
 */
export { api };

export const apiClient = {
  /**
   * Make a GET request
   *
   * @param {string} url - URL template with parameters
   * @param {object} params - URL parameters to substitute
   * @param {object} queryParams - Query parameters to append
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios promise
   * 
   * @example
   * apiClient.get(apiRoutes.CHARACTERS.DETAIL, { characterId: '123' })
   */
  get: (url, params = {}, queryParams = {}, config = {}) => {
    return api.get(substituteParams(url, params), {
      ...config,
      params: queryParams
    });
  },

  /**
   * Make a POST request
   * 
   * @param {string} url - URL template with parameters
   * @param {object} data - Request body
   * @param {object} params - URL parameters to substitute
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios promise
   * 
   * @example
   * apiClient.post(apiRoutes.CHARACTERS.VARIATIONS, { name: 'New Variation' }, { characterId: '123' })
   */
  post: (url, data = {}, params = {}, config = {}) => {
    return api.post(substituteParams(url, params), data, config);
  },

  /**
   * Make a PUT request
   * 
   * @param {string} url - URL template with parameters
   * @param {object} data - Request body
   * @param {object} params - URL parameters to substitute
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios promise
   */
  put: (url, data = {}, params = {}, config = {}) => {
    return api.put(substituteParams(url, params), data, config);
  },

  /**
   * Make a DELETE request
   * 
   * @param {string} url - URL template with parameters
   * @param {object} params - URL parameters to substitute
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios promise
   */
  delete: (url, params = {}, config = {}) => {
    return api.delete(substituteParams(url, params), config);
  },

  /**
   * Upload a file
   * 
   * @param {string} url - URL template with parameters
   * @param {FormData} formData - Form data with file
   * @param {object} params - URL parameters to substitute
   * @param {object} config - Additional axios config
   * @returns {Promise} Axios promise
   * 
   * @example
   * const formData = new FormData();
   * formData.append('file', fileObject);
   * apiClient.upload(apiRoutes.CHARACTERS.AVATAR, formData, { characterId: '123' })
   */
  upload: (url, formData, params = {}, config = {}) => {
    return api.post(substituteParams(url, params), formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  /**
   * Upload a file directly to S3 using a signed URL
   * 
   * @param {string} signedUrl - Pre-signed S3 URL
   * @param {File|Blob} file - File or blob to upload
   * @param {string} contentType - Content type of the file
   * @returns {Promise} Axios promise
   */
  uploadToS3: (signedUrl, file, contentType) => {
    return axios.put(signedUrl, file, {
      headers: {
        'Content-Type': contentType || file.type
      }
    });
  }
};

export default apiClient;
